import { DatePicker } from "@app/components/DatePicker.tsx";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { Grid2, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { QueryStatusRouter } from "bi-lib";
import moment from "moment";
import { useEffect, useRef } from "react";

interface Props {
    id?: number;
    setOnSave: (onSave: () => Promise<unknown>) => void;
}

export default function PersonalForm({ id, setOnSave }: Props) {
    const { staff, savePersonal } = useStaffData({ id });

    const defaultValues: any = {
        hiredDate: staff.data?.hiredDate ? moment(staff.data.hiredDate) : null,
        terminationDate: staff.data?.terminationDate ? moment(staff.data.terminationDate) : null,
        birthDate: staff.data?.birthDate ? moment(staff.data.birthDate) : null,
        spouseName: staff.data?.spouseName,
        emergContactName: staff.data?.emergContactName,
        emergContactPhone: staff.data?.emergContactPhone,
        socialSecurityNumber: staff.data?.socialSecurityNumber,
        notes: staff.data?.notes,
    };

    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) =>
            await savePersonal.mutateAsync({
                staffingId: id,
                hiredDate: value.hiredDate?.format("MM/DD/YYYY"),
                terminationDate: value.terminationDate?.format("MM/DD/YYYY"),
                birthDate: value.birthDate?.format("MM/DD/YYYY"),
                spouseName: value.spouseName,
                emergContactName: value.emergContactName,
                emergContactPhone: value.emergContactPhone,
                socialSecurityNumber: value.socialSecurityNumber,
                notes: value.notes,
            }),
    });

    const ref = useRef<HTMLFormElement>(null);

    const handleSave = async () => {
        if (ref.current?.reportValidity()) {
            await form.handleSubmit();
            return;
        }
        throw new Error("Form is invalid");
    };

    useEffect(() => {
        setOnSave(handleSave);
    }, [form]);

    return (
        <QueryStatusRouter queryHook={staff}>
            <form
                ref={ref}
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <Grid2 container spacing={1}>
                    <Grid2 size={4}>
                        <form.Field name={"hiredDate"}>
                            {(field) => (
                                <DatePicker
                                    value={field.state.value}
                                    label={"Hire Date"}
                                    onChange={(e) => field.handleChange(e)}
                                    sx={{ width: "100%" }}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"terminationDate"}>
                            {(field) => (
                                <DatePicker
                                    value={field.state.value}
                                    label={"Termination Date"}
                                    onChange={(e) => field.handleChange(e)}
                                    sx={{ width: "100%" }}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"birthDate"}>
                            {(field) => (
                                <DatePicker
                                    value={field.state.value}
                                    label={"Birth Date"}
                                    onChange={(e) => field.handleChange(e)}
                                    sx={{ width: "100%" }}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"socialSecurityNumber"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    label={"Social Security Number"}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"spouseName"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    label={"Spouses Name"}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"emergContactName"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    label={"Emergency Contact"}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"emergContactPhone"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    label={"Emergency Phone"}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={12}>
                        <form.Field name={"notes"}>
                            {(field) => (
                                <TextField
                                    value={field.state.value ?? ""}
                                    multiline
                                    minRows={3}
                                    label={"Notes"}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                </Grid2>
            </form>
        </QueryStatusRouter>
    );
}
